import { useState } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import { Loader } from "lucide-react";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import { createDiscount } from "../../services/schoolDiscountsServices"; // Import the service

export default function AddDiscountModal({
  setShowAddDiscountModal,
  getAllDiscounts,
}) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [discountName, setDiscountName] = useState("");

  // Discount-related states
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [selectedCurrency] = useState("IQD");

  const handleCancel = () => {
    setShowAddDiscountModal(false);
  };

  const handleDiscountTypeChange = (type) => {
    setDiscountType(type);
    setDiscountValue(0); // Reset value when changing type
  };

  const handleDiscountChange = (e) => {
    const value = e.target?.value ?? 0;
    if (discountType === "percentage" && parseFloat(value) > 100) {
      return;
    }
    setDiscountValue(value);
  };

  const handleNameChange = (e) => {
    setDiscountName(e.target.value);
  };

  const handleAddDiscount = async () => {
    if (!discountName.trim()) {
      setError("noDiscountName");
      return;
    }

    if (!discountValue || parseFloat(discountValue) <= 0) {
      setError("invalidDiscountValue");
      return;
    }

    try {
      setLoadingModal(true);
      setError(null);

      const discountData = {
        name: discountName,
        type: discountType,
        amount: discountType === "amount" ? parseFloat(discountValue) : 0,
        percentage:
          discountType === "percentage" ? parseFloat(discountValue) : 0,
      };

      const response = await createDiscount(discountData);

      if (response.status === 201) {
        setShowAddDiscountModal(false);
        getAllDiscounts();
      }
    } catch (err) {
      console.error("Error creating discount:", err);
      setError("discountCreationFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="addDiscount"
        onClose={handleCancel}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleAddDiscount}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
              />
            </section>
          </>
        }
      >
        <div className="o-column o-column--gap-size_400">
          <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400">
            <span className="u-min-width-specific_80">
              {t("discountName")}:
            </span>
            <div className="">
              <CustomInput
                name="discountName"
                value={discountName}
                onChange={handleNameChange}
                errorText={error === "noDiscountName" ? t(error) : null}
                flex={true}
                // placeholder={t("discountNamePlaceholder")}
              />
            </div>
          </div>

          {/* Discount type radio buttons */}
          <div className="o-column u-margin--bottom_100 o-column--gap-size_300">
            <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
              <input
                type="radio"
                name="discount"
                value="percentage"
                checked={discountType === "percentage"}
                onChange={() => handleDiscountTypeChange("percentage")}
              />
              <label htmlFor="percentageDiscount">
                {t("discountByPercentage")}
              </label>
            </div>
            <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
              <input
                type="radio"
                name="discount"
                value="amount"
                checked={discountType === "amount"}
                onChange={() => handleDiscountTypeChange("amount")}
              />
              <label htmlFor="amountDiscount">{t("discountByAmount")}</label>
            </div>
          </div>

          {/* Discount amount input */}
          <div className="o-row o-row--gap-size_700 o-row--vertical-align_center u-min-width-specific_400 u-padding--bottom_500">
            <span className="u-min-width-specific_80">{t("discount")}:</span>
            <div className="">
              <CustomInputWithUnit
                name="discountValue"
                value={discountValue}
                onChange={handleDiscountChange}
                onKeyDown={onlyNums}
                flex={true}
                unit={discountType === "percentage" ? "%" : t(selectedCurrency)}
                min={0}
                max={discountType === "percentage" ? 100 : undefined}
                type="moneyV2"
                errorText={error === "invalidDiscountValue" ? t(error) : null}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
